"use client";

import { useState, useEffect } from "react";
import { Tag } from "@/types";
import { fetchWithAuth } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Search, Plus } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { useDebounce } from "use-debounce";

export default function TagsPage() {
  const [tags, setTags] = useState<Tag[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebounce(searchQuery, 300);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newTagLabel, setNewTagLabel] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  const fetchTags = async () => {
    setIsLoading(true);
    try {
      const response = await fetchWithAuth(
        `/tags${
          debouncedSearchQuery
            ? `/search?q=${encodeURIComponent(debouncedSearchQuery)}`
            : ""
        }`
      );
      const tagsData = Array.isArray(response) ? response : response.data || [];
      setTags(tagsData);
      setTotal(
        Array.isArray(response)
          ? tagsData.length
          : response.pagination?.total || 0
      );
    } catch (error) {
      toast.error("Failed to fetch tags");
      setTags([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTags();
  }, [debouncedSearchQuery]);

  const handleAddTag = async () => {
    if (!newTagLabel.trim()) return;

    setIsProcessing(true);
    try {
      await fetchWithAuth("/tags", {
        method: "POST",
        body: JSON.stringify({
          label: newTagLabel.trim(),
          name: newTagLabel.trim().toLowerCase().replace(/\s+/g, "_"),
        }),
      });
      toast.success("Tag added successfully");
      setIsAddDialogOpen(false);
      setNewTagLabel("");
      fetchTags();
    } catch (error) {
      toast.error("Failed to add tag");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Tags</h1>
          <p className="text-muted-foreground">
            Manage tags for categorizing citizens
          </p>
        </div>
        <Button onClick={() => setIsAddDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Add Tag
        </Button>
      </div>

      <div className="flex items-center gap-2">
        <Search className="h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search tags..."
          className="max-w-sm"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Label</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Created Date</TableHead>
              <TableHead>Modified Date</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <div className="h-6 w-32 animate-pulse rounded bg-muted" />
                  </TableCell>
                  <TableCell>
                    <div className="h-6 w-32 animate-pulse rounded bg-muted" />
                  </TableCell>
                  <TableCell>
                    <div className="h-6 w-32 animate-pulse rounded bg-muted" />
                  </TableCell>
                  <TableCell>
                    <div className="h-6 w-32 animate-pulse rounded bg-muted" />
                  </TableCell>
                </TableRow>
              ))
            ) : tags.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="h-24 text-center">
                  No tags found
                </TableCell>
              </TableRow>
            ) : (
              tags.map((tag) => (
                <TableRow key={tag.tag_id}>
                  <TableCell>{tag.label}</TableCell>
                  <TableCell>{tag.name}</TableCell>
                  <TableCell>
                    {format(new Date(tag.created_date), "MMM d, yyyy")}
                  </TableCell>
                  <TableCell>
                    {tag.modified_date
                      ? format(new Date(tag.modified_date), "MMM d, yyyy")
                      : "Never"}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Add New Tag</DialogTitle>
            <DialogDescription>
              Create a new tag to categorize citizens. The tag name will be
              automatically generated from the label.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Input
                placeholder="Enter tag label..."
                value={newTagLabel}
                onChange={(e) => setNewTagLabel(e.target.value)}
              />
              {newTagLabel && (
                <p className="text-sm text-muted-foreground">
                  Name: {newTagLabel.toLowerCase().replace(/\s+/g, "_")}
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsAddDialogOpen(false)}
              disabled={isProcessing}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddTag}
              disabled={isProcessing || !newTagLabel.trim()}
            >
              {isProcessing ? "Adding..." : "Add Tag"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
